import { useCallback, useEffect, useRef, useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { keepAliveSessionPath, sessionPath } from '@/shared/lib/routes'
import apiClient from '@/shared/lib/apiClient'

import { t } from '@/shared/lib/i18n'
import type { SharedProps } from '@/shared/types/SharedProps'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Button } from '@/shared/ui/button'

// How long before the real server-session expiry we open the warning modal. The
// countdown shown to the user is the actual number of seconds left on the server
// session, so the client and server can never silently diverge (the previous design
// reset an independent client timer that did not extend the JWT, so "Stay logged in"
// could leave the user logged-in on screen but already expired on the server).
const WARNING_LEAD_SECONDS = 60
const TICK_MS = 1000

const nowInSeconds = () => Math.floor(Date.now() / 1000)

const SessionTimeoutModal = () => {
  const { sessionExpiresAt } = usePage<SharedProps>().props

  // "Stay logged in" extends the session out-of-band (keep_alive re-issues the JWT and
  // returns a later expiry) without a page reload, so its value can lead the prop until the
  // next Inertia visit catches up. Keep ONLY that out-of-band value in state.
  const [keepAliveExpiresAt, setKeepAliveExpiresAt] = useState<number | null>(null)
  const [isWarningOpen, setIsWarningOpen] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(WARNING_LEAD_SECONDS)
  const hasLoggedOutRef = useRef(false)

  // Single source of truth, derived in render — no prop→state syncing, so there is no
  // setState-in-render/effect that could cascade into React error #185 (REBUILD-289).
  // Both inputs only ever move the expiry forward (every authenticated request slides the
  // JWT; keep-alive re-issues it later still), so the furthest-out value is the live one.
  const propExpiresAt =
    typeof sessionExpiresAt === 'number' && sessionExpiresAt > 0 ? sessionExpiresAt : null
  const expiresAt =
    propExpiresAt === null && keepAliveExpiresAt === null
      ? null
      : Math.max(propExpiresAt ?? 0, keepAliveExpiresAt ?? 0)

  const logout = useCallback((timedOut: boolean) => {
    if (hasLoggedOutRef.current) return
    hasLoggedOutRef.current = true
    router.delete(sessionPath(), { data: { timed_out: timedOut } })
  }, [])

  const stayLoggedIn = useCallback(async () => {
    // Genuinely extend the session: keep_alive runs under authenticate_user!, which
    // re-issues the JWT, and returns the new expiry. This closes the old UX gap where
    // dismissing the modal did not actually keep the server session alive.
    try {
      const response = await apiClient.post(keepAliveSessionPath())
      const nextExpiresAt = response.data?.sessionExpiresAt
      if (typeof nextExpiresAt === 'number' && nextExpiresAt > 0) {
        setKeepAliveExpiresAt(nextExpiresAt)
        setIsWarningOpen(false)
      } else {
        logout(true)
      }
    } catch {
      // The session already expired (401/redirect) — treat as a timeout logout.
      logout(true)
    }
  }, [logout])

  useEffect(() => {
    if (expiresAt === null) return

    // A fresh expiry means the session was extended (navigation or keep-alive), so allow
    // the auto-logout to fire again for this new window.
    hasLoggedOutRef.current = false

    const intervalId = window.setInterval(() => {
      const remaining = expiresAt - nowInSeconds()

      if (remaining <= 0) {
        setSecondsLeft(0)
        setIsWarningOpen(true)
        logout(true)
        return
      }

      if (remaining <= WARNING_LEAD_SECONDS) {
        setSecondsLeft(remaining)
        setIsWarningOpen(true)
      } else if (isWarningOpen) {
        setIsWarningOpen(false)
      }
    }, TICK_MS)

    return () => window.clearInterval(intervalId)
  }, [expiresAt, isWarningOpen, logout])

  if (expiresAt === null) return null

  return (
    <Dialog open={isWarningOpen} onOpenChange={() => undefined}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('sessionTimeout.title')}</DialogTitle>
        </DialogHeader>
        <ul className="list-none space-y-1 text-sm text-muted-foreground">
          <li>{t('sessionTimeout.line1')}</li>
          <li>{t('sessionTimeout.line2')}</li>
          <li>{t('sessionTimeout.line3')}</li>
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => logout(true)}>
            {t('sessionTimeout.logOff')}
          </Button>
          <Button type="button" onClick={stayLoggedIn}>
            {t('sessionTimeout.stayLoggedIn', { seconds: secondsLeft })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default SessionTimeoutModal
